<script lang="ts">
  import BookmarkRow from './BookmarkRow.svelte';
  import DiscoveryResultRow from './DiscoveryResultRow.svelte';
  import type {
    PoiView,
    TextSearchResult,
    GroupView,
  } from '~/shared/api.js';

  // Two modes:
  // - 'bookmarks': renders the trip's filtered POIs (the user's saved
  //   places). Click a row → caller pans the map + opens that POI's
  //   bookmark popup.
  // - 'discovery': renders the current text-search results during
  //   discovery mode. Click a row → caller opens the discovery preview.
  // Mode is implicit from which array the caller provides — the sidebar
  // doesn't need a separate flag.
  let {
    headerLabel,
    bookmarks = null,
    discoveryResults = null,
    groups = [],
    focusedPlaceId = null,
    emptyMessage = null,
    onPickBookmark,
    onPickDiscoveryResult,
  }: {
    headerLabel: string;
    bookmarks?: PoiView[] | null;
    discoveryResults?: TextSearchResult[] | null;
    groups?: GroupView[];
    focusedPlaceId?: string | null;
    /** Shown when both lists are empty (e.g. filtered to zero results). */
    emptyMessage?: string | null;
    onPickBookmark?: (poi: PoiView) => void;
    onPickDiscoveryResult?: (result: TextSearchResult) => void;
  } = $props();

  let listEl: HTMLElement | null = $state(null);
  // When a pin on the map is selected (parent sets focusedPlaceId),
  // scroll the matching row into view inside the sidebar.
  $effect(() => {
    if (focusedPlaceId === null || !listEl) return;
    const el = listEl.querySelector(
      `[data-place-id="${CSS.escape(focusedPlaceId)}"]`,
    );
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  });

  const isEmpty = $derived(
    (bookmarks?.length ?? 0) === 0 && (discoveryResults?.length ?? 0) === 0,
  );
</script>

<aside class="sidebar" aria-label="Map results">
  <header class="header">
    <span class="header-label">{headerLabel}</span>
  </header>
  <div class="list" bind:this={listEl}>
    {#if isEmpty}
      {#if emptyMessage}
        <p class="empty">{emptyMessage}</p>
      {/if}
    {:else if discoveryResults !== null && discoveryResults.length > 0}
      {#each discoveryResults as r (r.placeId)}
        <DiscoveryResultRow
          result={r}
          focused={focusedPlaceId === r.placeId}
          onClick={() => onPickDiscoveryResult?.(r)}
        />
      {/each}
    {:else if bookmarks !== null}
      {#each bookmarks as poi (poi.id)}
        <BookmarkRow
          poi={poi}
          groups={groups}
          focused={focusedPlaceId === poi.placeId}
          onClick={() => onPickBookmark?.(poi)}
        />
      {/each}
    {/if}
  </div>
</aside>

<style>
  /* Mobile-first: hidden by default, shown only at desktop. */
  .sidebar {
    flex: none;
    width: 360px;
    align-self: stretch;
    display: none;
    flex-direction: column;
    background: var(--color-canvas);
    border: 1px solid var(--color-hairline);
    border-radius: var(--radius-md);
    overflow: hidden;
  }
  @media (min-width: 768px) {
    .sidebar { display: flex; }
  }
  .header {
    flex: none;
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-hairline-soft);
  }
  .header-label {
    font-size: 13px;
    color: var(--color-muted);
    font-weight: 500;
  }
  .list {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .empty {
    margin: 0;
    padding: 24px 16px;
    text-align: center;
    font-size: 13px;
    color: var(--color-muted);
  }
</style>
